import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterOutlet,
  ActivatedRoute,
} from '@angular/router';
import { Title } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { IconSetService } from '@coreui/icons-angular';
import { iconSubset, iconMaterial } from './icons/icon-subset';
import {
  Auth,
  authState,
  User,
  isSignInWithEmailLink,
  signInWithEmailLink,
  onIdTokenChanged,
} from '@angular/fire/auth';

import { interval, Subject, Subscription, take, takeUntil } from 'rxjs';
import { UserProfileService } from './services/user-profile.service';
import { SystemService } from './services/system.service';
import { SplashScreenComponent } from './components/splash-screen/splash-screen.component';
import { SplashScreenStateService } from './services/splash-screen-state.service';
import { NotificationService } from './services/notification.service';
import { SwUpdate } from '@angular/service-worker';
import { ContractsService } from './services/contracts.service';
import { OrdersService } from './services/orders.service';
import { MessagingService } from './services/messaging.service';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry, MatIconModule } from '@angular/material/icon';
import { DebugLoggerService } from './services/debug-logger.service';
import { AuthService } from './services/auth.service';
import { environment } from '../environments/environment';

// template: '<router-outlet /><app-splash-screen/>',
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet, CommonModule, SplashScreenComponent, MatIconModule],
  standalone: true,
})
export class AppComponent implements OnInit, OnDestroy {
  // Test comment added by Claude Code
  private auth: Auth = inject(Auth);
  private router = inject(Router);
  private titleService = inject(Title);
  private userProfile = inject(UserProfileService);
  private systemService = inject(SystemService);
  private orderService = inject(OrdersService);
  private contractService = inject(ContractsService);
  private notificationService = inject(NotificationService);
  private splashScreenStateService = inject(SplashScreenStateService);
  private swUpdate = inject(SwUpdate);
  private fcm = inject(MessagingService);
  private iconSetService = inject(IconSetService);
  private activatedRoute = inject(ActivatedRoute);
  private debugLogger = inject(DebugLoggerService);
  private authService = inject(AuthService);


  title = 'LAKAL Trading Platform';
  authState$ = authState(this.auth);
  authStateSubscription: Subscription;
  private emailViaLinkFlag: boolean = false;
  private userExistenceCheckInterval: any;
  private tokenChangeUnsubscribe: any;
  private _destroy$ = new Subject<void>();
  //test

  constructor() {
    // Add global error handler to ensure splash screen is hidden on errors
    window.addEventListener('error', (event) => {
      console.error('APP: Global error caught', event);
      this.splashScreenStateService.stop();
    });
    
    window.addEventListener('unhandledrejection', (event) => {
      console.error('APP: Unhandled promise rejection', event);
      this.splashScreenStateService.stop();
    });
    
    // Check for white screen recovery
    const lastPageLoad = localStorage.getItem('lastPageLoad');
    const now = Date.now();
    if (lastPageLoad) {
      const timeSinceLastLoad = now - parseInt(lastPageLoad);
      // If the page was loaded less than 2 seconds ago, it might be a refresh loop
      if (timeSinceLastLoad < 2000) {
        console.warn('APP: Possible refresh loop detected, clearing state');
        // Clear potentially problematic state more thoroughly
        localStorage.removeItem('emailForSignIn');
        localStorage.removeItem('lastLogin');
        sessionStorage.clear();
        
        // Clear Firebase Auth persistence to fix corrupted auth state
        try {
          // Clear IndexedDB (Firebase Auth persistence)
          if ('indexedDB' in window) {
            indexedDB.databases().then((databases) => {
              databases.forEach((db) => {
                if (db.name && db.name.includes('firebase')) {
                  indexedDB.deleteDatabase(db.name);
                  console.log('APP: Cleared Firebase IndexedDB:', db.name);
                }
              });
            });
          }
        } catch (error) {
          console.error('APP: Error clearing Firebase persistence:', error);
        }
      }
    }
    localStorage.setItem('lastPageLoad', now.toString());
    
    // Add recovery mechanism for stuck splash screen
    // This acts as a backup to the resolver - only kicks in if resolver fails
    // Check every 3 seconds for stuck splash screen
    let splashCheckCount = 0;
    const maxSplashChecks = 6; // 18 seconds total (aligns with 15s max timeout + buffer)
    const splashCheckInterval = setInterval(() => {
      splashCheckCount++;
      const splashElement = document.querySelector('app-splash-screen');
      
      if (splashElement && window.getComputedStyle(splashElement).display !== 'none') {
        console.log(`APP: Splash screen check ${splashCheckCount}/${maxSplashChecks} - still visible after ${splashCheckCount * 3} seconds`);
        
        // Force hide after 18 seconds (6 checks at 3-second intervals)
        // This is a BACKUP mechanism - resolver should normally handle hiding
        if (splashCheckCount >= maxSplashChecks) {
          console.error('APP: Splash screen stuck after 18 seconds, forcing hide as backup');
          this.splashScreenStateService.stop();
          // Also try to hide it via DOM manipulation as a last resort
          (splashElement as HTMLElement).style.display = 'none';
          clearInterval(splashCheckInterval);
        }
      } else {
        // Splash screen is hidden, stop checking
        console.log('APP: Splash screen hidden successfully');
        clearInterval(splashCheckInterval);
      }
    }, 3000);
    
    this.titleService.setTitle(this.title);
    // iconSet singleton
    this.iconSetService.icons = { ...iconSubset };
    

    // setting up the material icons
    const matIconRegistry = inject(MatIconRegistry);
    const domSanitizer = inject(DomSanitizer);
    iconMaterial.forEach((icon) => {
      matIconRegistry.addSvgIcon(
        icon.name,
        domSanitizer.bypassSecurityTrustResourceUrl(icon.url)
      );
    });

    console.log('APP: initial routing', this.router.url, location.href);
    
    // Set up ID token change listener for immediate detection of deleted users
    this.tokenChangeUnsubscribe = onIdTokenChanged(this.auth, async (user) => {
      if (user) {
        try {
          // Force token refresh to check if user still exists in Firebase Auth
          await user.getIdToken(true);
        } catch (error) {
          console.log('APP: Token refresh failed, user may have been deleted', error);
          this.handleDeletedUser();
        }
      }
    });
    
    // setting up authStateSubscription;
    this.authStateSubscription = this.authState$.pipe(takeUntil(this._destroy$)).subscribe(
      (aUser: User | null) => {
        try {
          //handle auth state changes here. Note, that user will be null if there is no currently logged in user.
          if (aUser !== null) {
            // someone is logged in
            this.debugLogger.enableForUser(aUser.email || '', '', '');
            this.debugLogger.log('info', 'AppComponent', 'authState', 'User logged in', {
              uid: aUser.uid,
              email: aUser.email,
              displayName: aUser.displayName
            });
            
            // Initialize deletion listener for this user
            this.authService.initializeDeletionListener(aUser.uid);

            this.checkLastLogin();
            this.userProfile.checkUser(aUser).then(
              (result: any) => {
              if (result['success'] == true && result['msg'] != 'a new user profile is created') {
                console.log('APP: user profile check');
                
                // Re-enable logger with full user info
                const userProf = this.userProfile.userProfile;
                
                // Check if profile is actually complete (user agreed to privacy policy)
                if (!userProf.agreePrivacyPolicy) {
                  console.log('[PROFILE-REDIRECT] Profile exists but incomplete - no privacy policy agreement');
                  this.splashScreenStateService.stop();
                  this.router.navigate(['/profile']);
                  return;
                }
                this.debugLogger.enableForUser(
                  userProf.email || aUser.email || '',
                  userProf.userID || '',
                  ''
                );
                
                this.debugLogger.log('info', 'AppComponent', 'checkUser', 'User profile loaded', {
                  userID: userProf.userID,
                  email: userProf.email,
                  nameFirst: userProf.nameFirst,
                  nameLast: userProf.nameLast,
                  validated: userProf.validated
                });
                
                this.userProfile.checkBusiness().then((result: any) => {
                  // stop the splash screen after the user profile and business check
                  this.splashScreenStateService.stop();

                  if (result['success'] == true) {
                    console.log('APP: business check');
                    const business = this.userProfile.business;
                    
                    // Final logger update with business info
                    this.debugLogger.enableForUser(
                      userProf.email || aUser.email || '',
                      userProf.userID || '',
                      business.businessID || ''
                    );
                    
                    this.debugLogger.log('info', 'AppComponent', 'checkBusiness', 'Business loaded', {
                      businessID: business.businessID,
                      businessName: business.businessName,
                      validated: business.validated,
                      businessUserResult: result
                    });
                    
                    this.loadMarketplace(this.userProfile.business.businessID);
                    this.loadFCM();
                    this.startUserExistenceCheck();
                  } else {
                    console.log('APP: no business');
                    this.debugLogger.log('warn', 'AppComponent', 'checkBusiness', 'No business found', {
                      result,
                      userID: userProf.userID
                    });
                  }
                });
              } else {
                // hide the splash screen if there is no user profile
                this.splashScreenStateService.stop();
                console.log('[PROFILE-REDIRECT] No user profile found');
                console.log('[PROFILE-REDIRECT] User authenticated:', aUser?.uid);
                console.log('[PROFILE-REDIRECT] loggedIn flag:', this.userProfile.loggedIn);
                console.log('[PROFILE-REDIRECT] Attempting redirect to /profile');
                this.debugLogger.log('warn', 'AppComponent', 'checkUser', 'No user profile found, redirecting to profile page', {
                  result
                });
                // Don't reset user - they are authenticated, just missing profile
                // Redirect to profile page which will prompt them to complete their profile
                this.router.navigate(['/profile']).then(
                  (success) => console.log('[PROFILE-REDIRECT] Navigation success:', success),
                  (error) => console.log('[PROFILE-REDIRECT] Navigation error:', error)
                );
              }
            },
            (err) => {
              // hide the splash screen for any error
              this.splashScreenStateService.stop();
              console.log('APP: got a user Error', err);
              this.debugLogger.log('error', 'AppComponent', 'checkUser', 'Error checking user', {
                error: err?.message || err
              });
              this.userProfile.resetUser();
            }
          );
          if (this.emailViaLinkFlag) {
            console.log('APP: emailViaLinkFlag', this.emailViaLinkFlag);
            this.emailViaLinkFlag = false;
            // remove while debugging
            setTimeout(() => {
              console.log('APP: emailViaLinkFlag redirecting to market');
              this.router.navigateByUrl('/market');
            }, 2000); // give some time to see the login-return page
          }
        } else {
          // there is no logged in user
          this.userProfile.resetUser();
          localStorage.removeItem('lastLogin');
          this.unloadMarketplace();
          this.stopUserExistenceCheck();
          this.systemService.initializeForAnonymousUsers().then(() => {
            // Hide splash screen immediately after anonymous initialization completes
            console.log('APP: Anonymous user initialization complete, hiding splash screen');
            this.splashScreenStateService.stop();
          }).catch((error) => {
            console.error('APP: Error initializing for anonymous users:', error);
            // Hide splash screen even on error
            this.splashScreenStateService.stop();
          });
          
          // Fallback timeout for anonymous users (reduced from 2000ms to 1000ms)
          setTimeout(() => {
            console.log('APP: Fallback timeout for anonymous users, hiding splash screen');
            this.splashScreenStateService.stop();
          }, 1000);
        }
      } catch (error) {
        // Catch any errors in auth state handling
        console.error('APP: Critical error in auth state handler:', error);
        this.debugLogger.log('error', 'AppComponent', 'authState', 'Critical error in auth state handler', {
          error: error instanceof Error ? error.message : String(error)
        });
        // Always hide splash screen on error
        this.splashScreenStateService.stop();
        
        // Try to recover by resetting user state
        this.userProfile.resetUser();
        this.unloadMarketplace();
        this.systemService.initializeForAnonymousUsers();
      }
    },
    (error) => {
      // Error in auth state subscription
      console.error('APP: Auth state subscription error:', error);
      this.splashScreenStateService.stop();
    });

    // load lookups
    console.log('App: loading lookups');
    // console.log("Barangays", this.systemService.getBarangays());
    //console.log("APP: href", location.href)
    const paramMatch = location.href.match(/\?(.+)/);
    if (paramMatch) {
      this.checkForEmailLink(location.href, paramMatch);
    }
  }

  ngOnInit(): void {
    console.log('APP: ngOnInit started'); // Add this too for visibility
    
    // iconSet singleton
    this.iconSetService.icons = { ...iconSubset };
    
    // Log the Firebase project being used
    console.log('🔥 Firebase Project ID:', environment.firebaseConfig.projectId);
    console.log('🔥 Using Emulators:', environment.useEmulators);
    
    // Failsafe: Ensure splash screen is hidden after maximum wait time
    const maxSplashTimeout = setTimeout(() => {
      console.warn('APP: Maximum splash screen timeout reached in ngOnInit');
      this.splashScreenStateService.stop();
      
      // Also check if the splash screen element is still visible
      const splashElement = document.querySelector('app-splash-screen');
      if (splashElement) {
        (splashElement as HTMLElement).style.display = 'none';
      }
    }, 25000); // 25 seconds maximum wait
    
    // Clear timeout when splash screen is hidden
    this.splashScreenStateService.subscribe((state: any) => {
      if (state === false || state === 'stopFast') {
        clearTimeout(maxSplashTimeout);
      }
    });
    
    this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
    });
    console.log('swUpdate.isEnabled: ' + this.swUpdate.isEnabled);
    // swUpdate check for new version
    if (this.swUpdate.isEnabled) {
      this.setupServiceWorker();
      this.swUpdate.versionUpdates.pipe(takeUntil(this._destroy$)).subscribe(async (res) => {
        console.log('Version Update', res);
        switch (res.type) {
          case 'VERSION_DETECTED':
            this.systemService.doCallout(
              'New version available. Your app will be reloaded after update',
              'info'
            );
            // alert("New version available. Your app will be reloaded after update");
            break;
          case 'VERSION_READY':
            this.swUpdate.activateUpdate().then((result) => {
              if (result) {
                console.log('New version ready for activation', result);
                // alert(
                //   'New version ready for activation. Your app will now reload'
                // );
                this.systemService.doCallout(
                  'New version ready for activation. Your app will now reload',
                  'info'
                );
              } else {
                // alert('Failed to update app version'); // hide this later on
                console.log('No new version detected');
              }
              alert('Reloading app'); // hide this later on
              window.location.reload();
            });
            break;
          default:
            break;
        }
      });
    }
    // it is meant to ensure that UI/UX starts with home page first

    // if the current location is /#login-return then redirect to login-return page
    // if the first location is payment-success then redirect to payment-success page

    const firstLocation = location.href.slice(window.origin.length);
    console.log('APP: ngOnInit initial routing', location.href, firstLocation);
    
    // Only handle specific cases that need special treatment
    // Do NOT redirect for regular navigation
    if (firstLocation === '/payment-success') {
      console.log('APP: payment-success page');
      this.splashScreenStateService.stopFast();
      this.router.navigate(['/payment-success']);
    } else if (firstLocation === '/#/login-return') {
      console.log('APP: login-return page');
      // Let the normal routing handle this
    } else if (firstLocation === '/' || firstLocation === '') {
      // Only redirect to tracker if we're at the root with no hash
      console.log('APP: root page, redirecting to tracker');
      this.router.navigate(['/tracker']);
    } else {
      // For all other routes, let Angular handle the routing naturally
      console.log('APP: existing route, no redirect needed', firstLocation);
    }
  }
  
  ngOnDestroy() {
    // when manually subscribing to an observable remember to unsubscribe in ngOnDestroy
    this.authStateSubscription.unsubscribe();
    this.stopUserExistenceCheck();
    if (this.tokenChangeUnsubscribe) {
      this.tokenChangeUnsubscribe();
    }
    this._destroy$.next();
    this._destroy$.complete();
  }

  checkForEmailLink(href: string, paramMatch: string[]) {
    const params = paramMatch[1];

    if (isSignInWithEmailLink(this.auth, href)) {
      console.log('APP: Caught the signInWithEmailLink');

      // The client SDK will parse the code from the link for you.
      let emailForSignIn = localStorage.getItem('emailForSignIn') || '';
      signInWithEmailLink(this.auth, emailForSignIn, href)
        .then((result) => {
          console.log('APP: signIn');
          // Clear email from storage.
          window.localStorage.removeItem('emailForSignIn');
          this.emailViaLinkFlag = true;
          // You can access the new user by importing getAdditionalUserInfo
          // and calling it with result:
          // getAdditionalUserInfo(result)
          // You can access the user's profile via:
          // getAdditionalUserInfo(result)?.profile
          // You can check if the user is new or existing:
          // getAdditionalUserInfo(result)?.isNewUser
        })
        .catch((error) => {
          console.log('App: Error with signInLink:', error);
          // Some error occurred, you can inspect the code: error.code
          // Common errors could be invalid email and invalid or expired OTPs.
        });
    } else {
      console.log(
        'APP: did not get an EmailLink',
        window.location.href,
        window.location.search
      );
    }
  }

  private setupServiceWorker() {
    if (!this.swUpdate.isEnabled) return;
    
    // Check less frequently
    interval(3600000).pipe( // 1 hour
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this.swUpdate.checkForUpdate();
    });
  }

  async loadMarketplace(businessID: string = '') {
    console.log('APP: loading marketplace', businessID);
    this.debugLogger.log('info', 'AppComponent', 'loadMarketplace', 'Starting marketplace load', {
      businessID,
      userID: this.userProfile.userProfile.userID
    });
    
    // open the spinner
    console.log('APP: loading contracts');
    this.debugLogger.log('info', 'AppComponent', 'loadMarketplace', 'Loading contracts...');
    
    // Use Promise.all for performance optimization from origin/main
    const loadPromises = [
      this.contractService.watchAllMyBuyContracts(businessID),
      this.contractService.watchAllMySellContracts(businessID),
      this.orderService.watchAllMyBuyOrders(businessID),
      this.orderService.watchAllMySellOrders(businessID),
      this.orderService.watchAllMyOffers(businessID)
    ];
    
    await Promise.all(loadPromises);
    
    console.log('APP: loading notifications');
    // Notifications are stored by userID, not businessID (fixed from HEAD)
    const userID = this.userProfile.userProfile.userID;
    console.log('APP: loading notifications for userID:', userID);
    await this.notificationService.readMyNotifications(userID);
    
    this.debugLogger.log('info', 'AppComponent', 'loadMarketplace', 'Marketplace load complete', {
      businessID,
      userID
    });
    // close the spinner
  }

  unloadMarketplace() {
    console.log('APP: unloading marketplace');
    this.contractService.unwatchAllMyBuyContracts();
    this.contractService.unwatchAllMySellContracts();
    this.orderService.unwatchAllMyBuyOrders();
    this.orderService.unwatchAllMySellOrders();
    this.orderService.unwatchAllMyOffers();
    const userID = this.userProfile.userProfile.userID;
    this.notificationService.unreadMyNotifications(userID);
  }

  async loadFCM() {
    try {
      console.log('APP: loading FCM');
      const businessID = this.userProfile.business.businessID;
      console.log('APP: FCM for business ID:', businessID);

      // First check if we already have a token
      const result: any = await this.userProfile.getUserFCMToken(businessID);
      if (result['success']) {
        console.log('APP: Existing FCM token found');
        return;
      }

      console.log('APP: No existing token, requesting FCM permission and generating token');
      await this.fcm.request();
      
      // Now that permission is granted and token generation started, subscribe to get the token
      console.log('APP: Subscribing to FCM token observable');
      this.fcm.token$.subscribe({
        next: (token) => {
          console.log('APP: FCM token received, saving to Firestore');
          if (token) {
            this.userProfile.setUserFCMToken(businessID, token);
          } else {
            console.warn('APP: FCM token is null or empty');
          }
        },
        error: (error) => {
          console.error('APP: Error in FCM token subscription:', error);
        }
      });
      
      // Subscribe to FCM messages
      this.fcm.message$.subscribe((message) => {
        console.log('APP: FCM message received:', message);
        //this.notificationService.addNotification(message);
      });
    } catch (err) {
      console.log('APP: error loading FCM', err);
    }
  }
  unloadFCM() {
    console.log('APP: unloading FCM');
    // this needs some work. not even sure if FCM is working
  }
  checkLastLogin() {
    const nowDate = new Date();
    const currentLoginTime = nowDate.getTime();
    let lastLogin = localStorage.getItem('lastLogin');
    if (lastLogin) {
      console.log('APP: last login', lastLogin);
      const lastLoginTime = parseInt(lastLogin);
      const timeDiff = currentLoginTime - lastLoginTime;
      console.log('APP: time diff', timeDiff);
      // save the new lastLogin
      localStorage.setItem('lastLogin', currentLoginTime.toString());
      // check now if they have been logged out for too long
      if (timeDiff > 1000 * 60 * 60 * 8) {
        // more than 8 hours
        console.log('APP: more than 8 hours');
        // logout the user and alert them that they have been logged out
        this.auth
          .signOut()
          .then((result) => {
            console.log('APP: logged out', result);
            this.systemService.doCallout(
              'You have been logged out due to inactivity',
              'warning'
            );
            // alert("You have been logged out due to inactivity");
            setTimeout(() => {
              window.location.reload();
            }, 5000);
          })
          .catch((err) => {
            console.log('APP: logout error', err);
          });
      } else {
        console.log('APP: less than 8 hours');
      }
    } else {
      console.log('APP: no last login');
      localStorage.setItem('lastLogin', currentLoginTime.toString());
    }
  }

  startUserExistenceCheck() {
    // Check every 30 seconds if the user still exists
    this.userExistenceCheckInterval = setInterval(async () => {
      try {
        const currentUser = this.auth.currentUser;
        if (currentUser) {
          // Verify the user profile still exists
          const userExists = await this.userProfile.verifyUserExists(currentUser.uid);
          if (!userExists) {
            console.log('APP: User has been deleted, forcing logout');
            this.debugLogger.log('warn', 'AppComponent', 'userExistenceCheck', 'User has been deleted, forcing logout');
            
            // Clear local data
            this.userProfile.resetUser();
            localStorage.removeItem('lastLogin');
            
            // Sign out from Firebase Auth
            await this.auth.signOut();
            
            // Show notification
            this.systemService.doCallout(
              'Your account has been deleted by an administrator',
              'error'
            );
            
            // Redirect to login after a delay
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 3000);
          }
        }
      } catch (error) {
        console.error('APP: Error checking user existence:', error);
        this.debugLogger.log('error', 'AppComponent', 'userExistenceCheck', 'Error checking user existence', {
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }, 30000); // Check every 30 seconds
  }

  stopUserExistenceCheck() {
    if (this.userExistenceCheckInterval) {
      clearInterval(this.userExistenceCheckInterval);
      this.userExistenceCheckInterval = null;
    }
  }

  private async handleDeletedUser() {
    console.log('APP: User has been deleted, forcing logout');
    this.debugLogger.log('warn', 'AppComponent', 'handleDeletedUser', 'User has been deleted, forcing logout');
    
    // Clear local data
    this.userProfile.resetUser();
    localStorage.removeItem('lastLogin');
    
    // Show notification
    this.systemService.doCallout(
      'Your account has been deleted by an administrator',
      'error'
    );
    
    // Try to sign out (may fail if user already deleted)
    try {
      await this.auth.signOut();
    } catch (error) {
      console.log('APP: Error signing out deleted user:', error);
    }
    
    // Redirect to login after a delay
    setTimeout(() => {
      this.router.navigate(['/login']);
    }, 3000);
  }
}
